import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { eventBus } from '@atc/cai-event-bus';
import { createChainedFunction } from '@atc/create-chained-function';

import { Button } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxMakeOfferClick, psxVDPChatSellerClick, psxVDPTestDriveClick } from 'reaxl-analytics-handlers';

import authDuck from '@/ducks/authDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

function PsxButton({
    uiContext = 'makeOffer',
    buttonSize,
    buttonStyle = 'primary',
    children,
    block,
    loadingComponent = null,
    onClick,
    placement,
    ...rest
}) {
    const isWebComponentReady = useSelector(vdpResultsDuck.selectors.getIsWebComponentReady);
    const { id: inventoryId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const isHasChatPreviouslyVdpListing = useSelector((currentState) => userDuck.selectors.isHasChatPreviouslyVdpListing(currentState, inventoryId)) || false;
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const dispatch = useDispatch();

    // Handler for makeOffer
    const handleMakeOffer = (event) => {
        eventBus.emit('psx.listingActions.openMakeOffer', {});
        sendClick(psxMakeOfferClick, event, { inventoryId, placement });
    };

    // Handler for testDrive
    const handleScheduleTestDrive = (event) => {
        eventBus.emit('psx.listingActions.openTestDrive', {});
        sendClick(psxVDPTestDriveClick, event, { inventoryId, placement });
    };

    // Handler for chat
    const handleChatWithSeller = (event) => {
        eventBus.emit('psx.listingActions.openChat', {});

        if (!isHasChatPreviouslyVdpListing && isLoggedIn) {
            sendClick(psxVDPChatSellerClick, event, { inventoryId });
            dispatch(userDuck.creators.updateHasChatPreviouslyVdpListings(inventoryId));
        }
    };

    // Determine onClick handler based on uiContext
    let onClickHandler;
    switch (uiContext) {
        case 'makeOffer':
            onClickHandler = handleMakeOffer;
            break;
        case 'testDrive':
            onClickHandler = handleScheduleTestDrive;
            break;
        case 'chat':
            onClickHandler = handleChatWithSeller;
            break;
        default:
            onClickHandler = undefined;
    }
    onClickHandler = createChainedFunction(onClickHandler, onClick);

    if (!isWebComponentReady) {
        return loadingComponent;
    }

    return (
        <Button
            bsSize={buttonSize}
            bsStyle={buttonStyle}
            onClick={onClickHandler}
            block={block}
            {...rest}
        >
            {children}
        </Button>
    );
}

PsxButton.propTypes = {
    /**
     * The context that determines which PSX action this button will trigger.
     */
    uiContext: PropTypes.oneOf(['makeOffer', 'testDrive', 'chat']),
    /**
     * The size of the button following Bootstrap sizing conventions.
     */
    buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
    /**
     * The visual style of the button following Bootstrap styling conventions.
     */
    buttonStyle: PropTypes.oneOf(['primary', 'secondary', 'primary-inverse']),
    /**
     * The content to be displayed inside the button (text, icons, etc.).
     */
    children: PropTypes.node,
    /**
     * Whether the button should take the full width of its container.
     */
    block: PropTypes.bool,
    /**
     * Optional component to show while waiting for psx.action to be ready.
     * Helps keep the layout stable during the loading psx.action.
     * If null, nothing is shown during loading.
     */
    loadingComponent: PropTypes.node,
    /**
     * Additional click handler to be chained with the default PSX action.
     * This function will be called after the PSX-specific handler executes.
     */
    onClick: PropTypes.func,
    /**
     * Specifies the location context where this PsxButton is rendered.
     * Used for analytics tracking and tagging to identify button placement
     */
    placement: PropTypes.string,
};

export default PsxButton;
